import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { FullPageLoader } from "@/components/ui/full-page-loader";

interface OrderSummary {
  id: string;
  total: number;
  orderNumber: string;
  status: string;
  createdAt: string;
  customerName: string;
  invoiceUrl?: string | null;
}

export function OrderConfirmationPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { currentStore } = useCurrentStore();
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderSummary = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await api.get(`/api/orders/${orderId}/summary`);
        if (response.data?.success && response.data?.order) {
          setOrder(response.data.order);
        } else {
          setError("Order not found");
        }
      } catch (err: unknown) {
        console.error("Error fetching order summary:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load order information"
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (orderId) {
      fetchOrderSummary();
    }
  }, [orderId]);

  // Format price
  const formatPrice = (price: number) => {
    return price.toLocaleString("en-NG", {
      style: "currency",
      currency: currentStore?.currency || "NGN",
    });
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return <FullPageLoader text="Loading order" variant="page" />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-destructive/30">
          <CardHeader>
            <CardTitle className="text-destructive">Order Not Found</CardTitle>
            <CardDescription>
              We couldn't locate the order you're looking for.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate("/")}>Return to Homepage</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <Card className="border border-gray-200 overflow-hidden">
          {/* Success header */}
          <div className="bg-green-50 border-b border-green-100 px-6 py-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-9 w-9 text-green-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Order Placed!</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your order has been received and is being processed.
            </p>
          </div>

          <CardContent className="p-6 space-y-5">
            {order && (
              <>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Order Number</p>
                    <p className="font-semibold">{order.orderNumber}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                    <p className="font-medium">{formatDate(order.createdAt)}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                    <p className="font-semibold">{formatPrice(order.pricing?.total || order.total || 0)}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Status</p>
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200 capitalize">
                      {order.status}
                    </span>
                  </div>
                </div>

                <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-4 text-sm space-y-1.5">
                  <p className="text-gray-700">
                    We've sent a confirmation email with your order details.
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Your customer account has been created automatically. Use the same email for future orders.
                  </p>
                </div>
              </>
            )}
          </CardContent>

          <CardFooter className="px-6 pb-6 pt-0 flex flex-col sm:flex-row gap-2">
            <Button
              className="w-full sm:w-auto"
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Continue Shopping
            </Button>
            {order?.invoiceUrl && (
              <Button
                className="w-full sm:w-auto"
                variant="outline"
                size="sm"
                onClick={() => window.open(order.invoiceUrl!, "_blank")}
              >
                View Invoice
              </Button>
            )}
            <Button className="w-full sm:flex-1" size="sm" onClick={() => window.print()}>
              Print Receipt
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
